import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  CaretDownIcon,
  MagnifyingGlassIcon,
  XIcon,
} from "@phosphor-icons/react";
import { ChevronRight } from "lucide-react";
import { SimpleValueRenderer } from "../Components/FactRenderer";
import { ScalarKind } from "../types";
import {
  TreeNode,
  buildTree,
  filterTree,
  isLeafValue,
  pruneTree,
} from "./hierarchyTreeUtils";

interface HierarchicalDropdownProps {
  scalarType: ScalarKind | null;
  scalarUnit?: string | null;
  options: any[];
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
  filterExisting?: any[];
  // The trigger renders a <button>, not an input/textarea/select, so the
  // parent's `[&_input]:pl-24` / `[&_select]:pl-24` arbitrary-variant
  // selectors don't reach it. When the parent paints a leading Autofill
  // chip or a trailing Source chip over this input column, it passes
  // these flags through so the button's own padding clears the chips.
  hasAutofillChip?: boolean;
  hasSourceChip?: boolean;
}

const HierarchicalDropdown: React.FC<HierarchicalDropdownProps> = ({
  scalarType,
  scalarUnit,
  options,
  value,
  onChange,
  placeholder = "Select a value...",
  filterExisting = [],
  hasAutofillChip = false,
  hasSourceChip = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Build tree from options
  const fullTree = useMemo(() => buildTree(options), [options]);

  // Prune already-selected values
  const prunedTree = useMemo(
    () => pruneTree(fullTree, filterExisting, options),
    [fullTree, filterExisting, options]
  );

  // Apply search filter
  const displayTree = useMemo(
    () => filterTree(prunedTree, searchQuery),
    [prunedTree, searchQuery]
  );

  // Check if there are any available options after pruning
  const hasAvailableOptions = prunedTree.length > 0;

  // Position the dropdown portal
  useEffect(() => {
    if (!isOpen) {
      setDropdownPosition(null);
      return;
    }

    const anchor = buttonRef.current;
    if (!anchor) return;

    const updatePosition = () => {
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const dropdownWidth = Math.max(240, rect.width);
      const viewportMargin = 16;
      const desiredHeight = 300;
      const spaceBelow = window.innerHeight - rect.bottom - viewportMargin;
      const spaceAbove = rect.top - viewportMargin;
      const openUpward = spaceBelow < desiredHeight && spaceAbove > spaceBelow;

      if (openUpward) {
        setDropdownPosition({
          bottom: window.innerHeight - rect.top,
          left: rect.left,
          width: dropdownWidth,
          maxHeight: Math.max(0, Math.min(desiredHeight, spaceAbove)),
        });
      } else {
        setDropdownPosition({
          top: rect.bottom,
          left: rect.left,
          width: dropdownWidth,
          maxHeight: Math.max(0, Math.min(desiredHeight, spaceBelow)),
        });
      }
    };

    updatePosition();

    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(anchor);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  // Close on outside click. Registered directly in the effect that runs
  // after the open commit (#12187): by the time this effect runs, the click
  // that opened the dropdown has already finished dispatching, so there is
  // no risk of the same click immediately closing it -- no arming delay
  // needed.
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest(".hierarchical-tree-dropdown")) return;
      if (buttonRef.current?.contains(target)) return;

      setIsOpen(false);
      setSearchQuery("");
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen]);

  const handleSelect = (node: TreeNode) => {
    onChange(node.path);
    setIsOpen(false);
    setSearchQuery("");
  };

  const existingSet = useMemo(
    () => new Set(filterExisting.map((v) => JSON.stringify(v))),
    [filterExisting]
  );

  const renderNode = (node: TreeNode) => {
    const nodeKey = JSON.stringify(node.path);
    const isAlreadySelected = existingSet.has(nodeKey);
    const isSelectable = isLeafValue(node, options) && !isAlreadySelected;
    // The dropdown's current value (a path array) renders checked so the
    // open tree reflects what the cell already holds. Clicking that row
    // again (label or checkbox) toggles it off: the draft clears and the
    // dropdown stays open so another value can be picked. Whether an
    // empty value can actually be committed is the caller's rule (e.g.
    // the grid blocks clearing required columns). #11174.
    const isCurrentValue =
      Array.isArray(value) && nodeKey === JSON.stringify(value);
    const handleToggle = () => {
      if (isCurrentValue) {
        onChange(null);
      } else if (isSelectable) {
        handleSelect(node);
      }
    };
    const displayValue = node.path[node.path.length - 1];

    return (
      <div key={nodeKey}>
        <div
          className={`flex items-center gap-2 py-1 rounded select-none min-w-0 ${
            isSelectable ? "hover:bg-gray-100 cursor-pointer" : "cursor-default"
          }`}
          style={{ paddingLeft: `${node.level * 20 + 8}px` }}
          // Tree rows are non-focusable, so a bare click would move
          // focus to <body> — read by the grid's focus-loss handler as
          // leaving the editor. Swallow the mousedown's default so
          // clicking a row never shifts focus (#11174).
          onMouseDown={(e) => e.preventDefault()}
          // A click on the checkbox is the checkbox's: its change handler
          // toggles, and the same click then bubbles here. Handling it here
          // too dispatched the toggle twice, so the owner saw one pick as
          // two and, in the grid, sent it twice (#11978).
          onClick={(e) => {
            if ((e.target as Element).closest('input[type="checkbox"]')) {
              return;
            }
            handleToggle();
          }}
        >
          {isSelectable && (
            <input
              type="checkbox"
              checked={isCurrentValue}
              onChange={handleToggle}
              className="rounded border-gray-300 text-blue-600 focus:ring-[var(--fp-ink-3)] flex-shrink-0"
            />
          )}

          <span
            className={`text-sm truncate ${
              isAlreadySelected
                ? "text-gray-400 line-through"
                : isSelectable
                  ? "text-gray-900"
                  : "text-gray-500"
            }`}
          >
            <SimpleValueRenderer
              value={displayValue}
              scalarType={scalarType}
              scalarUnit={scalarUnit}
            />
          </span>
        </div>

        {node.children.map((child) => renderNode(child))}
      </div>
    );
  };

  if (!hasAvailableOptions) {
    return (
      <p className="text-sm text-gray-500 italic px-3 py-2">
        All available options have been selected
      </p>
    );
  }

  return (
    <>
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full ${hasAutofillChip ? "pl-24" : "pl-3"} ${
          hasSourceChip ? "pr-24" : "pr-3"
        } py-2 text-left text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--fp-ink-3)] focus:border-[var(--fp-ink-3)] flex items-center justify-between bg-white`}
      >
        {Array.isArray(value) && value.length > 0 ? (
          <span className="flex items-center gap-1 min-w-0 text-gray-900">
            {value.map((level, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                )}
                <span className="truncate">
                  <SimpleValueRenderer
                    value={level}
                    scalarType={scalarType}
                    scalarUnit={scalarUnit}
                  />
                </span>
              </React.Fragment>
            ))}
          </span>
        ) : (
          <span className="truncate text-gray-500">{placeholder}</span>
        )}
        <CaretDownIcon
          size={14}
          className={`ml-2 flex-shrink-0 text-gray-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Portalled Dropdown */}
      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            // React portal events bubble through the React tree, not the
            // DOM — without this, every click inside the tree also fires
            // the ancestors' handlers (e.g. the grid cell's onClick,
            // which toggles edit mode and unmounts this picker) (#11174).
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] flex flex-col hierarchical-tree-dropdown"
            style={{
              ...(dropdownPosition.top !== undefined
                ? { top: `${dropdownPosition.top}px` }
                : {}),
              ...(dropdownPosition.bottom !== undefined
                ? { bottom: `${dropdownPosition.bottom}px` }
                : {}),
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              maxHeight: `${dropdownPosition.maxHeight}px`,
            }}
          >
            {/* Search Bar */}
            <div className="p-2 border-b border-gray-100 flex-shrink-0">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  ref={(el) => {
                    searchInputRef.current = el;
                    // Focus once, when the box mounts (i.e. when the
                    // dropdown opens) -- a callback ref fires on mount, not
                    // on every re-render, so this replaces the deferred
                    // focus-after-render timer without racing it (#12187).
                    el?.focus();
                  }}
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-[var(--fp-ink-3)] focus:border-[var(--fp-ink-3)]"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-400 hover:text-gray-600"
                    type="button"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Tree Content */}
            <div className="overflow-y-auto flex-1 py-1">
              {displayTree.length === 0 ? (
                <div className="text-sm text-gray-500 px-3 py-2">
                  {searchQuery
                    ? `No items found matching "${searchQuery}"`
                    : "No values available"}
                </div>
              ) : (
                displayTree.map((node) => renderNode(node))
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default HierarchicalDropdown;
